import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useAuth } from '../components/auth/AuthProvider';
import { useFavorites } from '../hooks/useFavorites';
import { getProductById, FirestoreProduct } from '../firebase/firestore';
import { ShoppingCartIcon, DownloadIcon, ClockIcon, HeartIcon, BellIcon, MessageSquareIcon, SearchIcon, PackageIcon, StarIcon, XIcon, Loader2Icon, EyeIcon, PlayIcon } from 'lucide-react';
export function BuyerDashboard() {
  const [scrolled, setScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('purchases');
  const {
    isAuthenticated,
    user
  } = useAuth();
  const {
    favorites,
    toggleFavorite,
    favoritesCount,
    loading: favoritesLoading
  } = useFavorites();
  const navigate = useNavigate();
  // State for fetched favorite products
  const [favoriteProducts, setFavoriteProducts] = useState<FirestoreProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate('/login');
      return;
    }
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isAuthenticated, user, navigate]);
  // Fetch favorite products when favorites tab is active and favorites change
  useEffect(() => {
    if (activeTab !== 'favorites' || favorites.size === 0) {
      if (activeTab === 'favorites') setFavoriteProducts([]);
      return;
    }
    const fetchFavoriteProducts = async () => {
      setLoadingProducts(true);
      try {
        const productIds = Array.from(favorites);
        const products = await Promise.all(productIds.map(async id => {
          try {
            return await getProductById(id);
          } catch {
            return null;
          }
        }));
        setFavoriteProducts(products.filter((p): p is FirestoreProduct => p !== null));
      } catch (err) {
        console.error('Error fetching favorite products:', err);
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchFavoriteProducts();
  }, [activeTab, favorites]);
  if (!isAuthenticated || !user) return null;
  const getInitials = () => {
    if (user.name) return user.name.charAt(0).toUpperCase();
    if (user.email) return user.email.charAt(0).toUpperCase();
    return 'U';
  };
  const renderEmptyState = (icon: React.ReactNode, title: string, description: string) => <div className="bg-white dark:bg-[#162825] rounded-xl p-12 text-center shadow-sm border border-gray-100 dark:border-[#1A3330]" data-id="element-4209">
      <div className="w-16 h-16 bg-gray-50 dark:bg-[#1A3330] rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400 dark:text-[#7A8F8A]" data-id="element-4210">
        {icon}
      </div>
      <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-4211">
        {title}
      </h3>
      <p className="text-gray-500 dark:text-[#7A8F8A] max-w-md mx-auto" data-id="element-4212">
        {description}
      </p>
    </div>;
  const renderFavorites = () => {
    if (favoritesLoading || loadingProducts) {
      return <div className="bg-white dark:bg-[#162825] rounded-xl p-12 text-center shadow-sm border border-gray-100 dark:border-[#1A3330]" data-id="element-4213">
          <Loader2Icon className="h-8 w-8 animate-spin text-[#2DB489] dark:text-[#4ED1A0] mx-auto mb-4" data-id="element-4214" />
          <p className="text-gray-500 dark:text-[#7A8F8A]" data-id="element-4215">
            Loading favorites...
          </p>
        </div>;
    }
    if (favoriteProducts.length === 0) {
      return <div className="bg-white dark:bg-[#162825] rounded-xl p-12 text-center shadow-sm border border-gray-100 dark:border-[#1A3330]" data-id="element-4216">
          <div className="w-16 h-16 bg-gray-50 dark:bg-[#1A3330] rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400 dark:text-[#7A8F8A]" data-id="element-4217">
            <HeartIcon className="h-8 w-8" data-id="element-4218" />
          </div>
          <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-4219">
            No favorites yet
          </h3>
          <p className="text-gray-500 dark:text-[#7A8F8A] max-w-md mx-auto mb-6" data-id="element-4220">
            Items you mark as favorite while browsing will be saved here.
          </p>
          <button onClick={() => navigate('/browse')} className="inline-flex items-center px-5 py-2.5 bg-[#2DB489] dark:bg-[#4ED1A0] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity" data-id="element-4221">
            <SearchIcon className="h-4 w-4 mr-2" data-id="element-4222" />
            Browse Products
          </button>
        </div>;
    }
    return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-4223">
        {favoriteProducts.map(product => {
        const isVideo = product.isVideo || product.category === 'Short-form Video';
        const isVerticalVideo = product.videoMetadata?.isVertical || false;
        const sellerName = product.sellerName || 'Unknown';
        const rating = product.rating || 4.5;
        const isFree = product.price === 0 || product.isFree;
        return <div key={product.id} className="group relative bg-white dark:bg-[#162825] rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col h-full" data-id="element-4224">
              {/* Thumbnail — fixed aspect ratio */}
              <div className="relative aspect-[4/3] overflow-hidden cursor-pointer bg-black" onClick={() => navigate(`/product/${product.id}`)} data-id="element-4225">
                <img src={product.image || 'https://images.unsplash.com/photo-1561070791-2526d30994b5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80'} alt={product.title} className="w-full h-full object-cover transition-transform duration-400 ease-in-out group-hover:scale-105" onError={e => {
              e.currentTarget.src = 'https://images.unsplash.com/photo-1561070791-2526d30994b5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80';
            }} data-id="element-4226" />
                {/* Video play indicator */}
                {isVideo && <div className="absolute inset-0 flex items-center justify-center" data-id="element-4227">
                    <div className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center" data-id="element-4228">
                      <PlayIcon className="h-6 w-6 text-white" data-id="element-4229" />
                    </div>
                  </div>}
                <button onClick={e => {
              e.stopPropagation();
              if (product.id) toggleFavorite(product.id);
            }} className="absolute top-3 right-3 bg-white dark:bg-[#162825] rounded-full p-2 shadow-md transition-all duration-200 opacity-100" aria-label="Remove from favorites" data-id="element-4230">
                  <HeartIcon className="h-5 w-5 text-red-500 fill-red-500 cursor-pointer" data-id="element-4231" />
                </button>
                {/* Category tag */}
                {product.category && <div className="absolute top-3 left-3" data-id="element-4232">
                    <span className="bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full" data-id="element-4233">
                      {product.category}
                    </span>
                  </div>}
                {/* Video Type indicator */}
                {isVideo && <div className="absolute bottom-3 left-3" data-id="element-4234">
                    <span className="bg-purple-500 text-white text-xs font-bold px-2 py-1 rounded-full" data-id="element-4235">
                      {isVerticalVideo ? 'Vertical' : 'Horizontal'} Video
                    </span>
                  </div>}
                {/* Free badge */}
                {isFree && !isVideo && <div className="absolute bottom-3 left-3" data-id="element-4236">
                    <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full" data-id="element-4237">
                      FREE
                    </span>
                  </div>}
                {/* Trending badge */}
                {product.isTrending && <div className="absolute bottom-3 right-3" data-id="element-4238">
                    <span className="bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center" data-id="element-4239">
                      <span className="mr-1" data-id="element-4240">🔥</span>Trending
                    </span>
                  </div>}
                {/* New badge */}
                {product.isNewArrival && !product.isTrending && <div className="absolute bottom-3 right-3" data-id="element-4241">
                    <span className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center" data-id="element-4242">
                      <span className="mr-1" data-id="element-4243">✨</span>New
                    </span>
                  </div>}
              </div>
              {/* Content section — flex-1 pushes price row to bottom */}
              <div className="p-4 flex flex-col flex-1" data-id="element-4244">
                <div className="flex items-center justify-between mb-1.5" data-id="element-4245">
                  {/* Seller info */}
                  <div className="flex items-center" data-id="element-4246">
                    <div className="w-5 h-5 rounded-full bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] flex items-center justify-center text-white text-xs mr-2 flex-shrink-0" data-id="element-4247">
                      {sellerName.charAt(0)}
                    </div>
                    <span className="text-xs text-gray-600 dark:text-[#7A8F8A] truncate" data-id="element-4248">
                      by {sellerName}
                    </span>
                  </div>
                  {/* Rating */}
                  <div className="flex items-center flex-shrink-0" data-id="element-4249">
                    <StarIcon className="h-3.5 w-3.5 text-yellow-400 fill-current" data-id="element-4250" />
                    <span className="text-xs text-gray-600 dark:text-[#7A8F8A] ml-1" data-id="element-4251">
                      {rating}
                    </span>
                  </div>
                </div>
                <h3 className="text-base font-semibold mb-1 text-gray-800 dark:text-[#F8FAF9] cursor-pointer line-clamp-1" onClick={() => navigate(`/product/${product.id}`)} data-id="element-4252">
                  {product.title}
                </h3>
                <p className="text-xs text-gray-500 dark:text-[#7A8F8A] mb-2.5 line-clamp-2 min-h-[2rem]" data-id="element-4253">
                  {product.shortDescription || product.description}
                </p>
                {/* Tags — fixed height to prevent layout shift */}
                <div className="flex flex-wrap gap-1.5 mb-2.5 min-h-[24px]" data-id="element-4254">
                  {product.platform && <span className="text-xs bg-gray-100 dark:bg-[#1A3330] text-gray-600 dark:text-[#B0C1BD] px-1.5 py-0.5 rounded-full" data-id="element-4255">
                      {product.platform}
                    </span>}
                  {product.format && <span className="text-xs bg-gray-100 dark:bg-[#1A3330] text-gray-600 dark:text-[#B0C1BD] px-1.5 py-0.5 rounded-full" data-id="element-4256">
                      {product.format}
                    </span>}
                  {product.skillLevel && <span className="text-xs bg-gray-100 dark:bg-[#1A3330] text-gray-600 dark:text-[#B0C1BD] px-1.5 py-0.5 rounded-full" data-id="element-4257">
                      {product.skillLevel}
                    </span>}
                </div>
                {/* Price + buttons — always at bottom */}
                <div className="flex items-center justify-between mt-auto" data-id="element-4258">
                  <span className="text-base font-bold text-gray-900 dark:text-white" data-id="element-4259">
                    {isFree ? 'Free' : `$${product.price}`}
                  </span>
                  <div className="flex space-x-1" data-id="element-4260">
                    <button className="bg-[#0A3C52] dark:bg-[#0B3C45] text-white px-2.5 py-1.5 rounded-l-full text-xs font-medium flex items-center justify-center hover:bg-[#0D4B65] transition-colors" onClick={() => navigate(`/product/${product.id}`)} data-id="element-4261">
                      <EyeIcon className="h-3 w-3 mr-1" data-id="element-4262" />
                      Preview
                    </button>
                    <button className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white px-2.5 py-1.5 rounded-r-full text-xs font-medium hover:bg-[#25A077] transition-colors" onClick={() => navigate(`/product/${product.id}`)} data-id="element-4263">
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            </div>;
      })}
      </div>;
  };
  const renderTabContent = () => {
    switch (activeTab) {
      case 'purchases':
        return renderEmptyState(<ShoppingCartIcon className="h-8 w-8" data-id="element-4264" />, 'No purchases yet', 'Your purchased products will appear here once you complete a transaction.');
      case 'downloads':
        return renderEmptyState(<DownloadIcon className="h-8 w-8" data-id="element-4265" />, 'No downloads available', 'Products you have purchased and can download will show up here.');
      case 'orders':
        return renderEmptyState(<ClockIcon className="h-8 w-8" data-id="element-4266" />, 'No order history', 'You have not placed any orders yet.');
      case 'favorites':
        return renderFavorites();
      case 'notifications':
        return renderEmptyState(<BellIcon className="h-8 w-8" data-id="element-4267" />, 'No notifications', 'You are all caught up! New alerts will appear here.');
      case 'messages':
        return renderEmptyState(<MessageSquareIcon className="h-8 w-8" data-id="element-4268" />, 'No messages', 'Your conversations with sellers will be listed here.');
      default:
        return null;
    }
  };
  return <div className="min-h-screen bg-[#F8FAFA] dark:bg-[#0F1F1D] transition-colors duration-300" data-id="element-4269">
      <Navbar scrolled={scrolled} data-id="element-4270" />

      <main className="pt-24 pb-16" data-id="element-4271">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4272">
          {/* Header */}
          <div className="mb-8" data-id="element-4273">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-[#F8FAF9] mb-2" data-id="element-4274">
              Buyer Dashboard
            </h1>
            <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-4275">
              Manage your purchases and downloads
            </p>
          </div>

          {/* Profile Card */}
          <div className="bg-white dark:bg-[#162825] rounded-xl shadow-sm border border-gray-100 dark:border-[#1A3330] p-6 mb-8" data-id="element-4276">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6" data-id="element-4277">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6" data-id="element-4278">
                <div className="w-20 h-20 rounded-full bg-[#2DB489] flex items-center justify-center text-white text-3xl font-bold flex-shrink-0" data-id="element-4279">
                  {getInitials()}
                </div>
                <div data-id="element-4280">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-[#F8FAF9]" data-id="element-4281">
                    {user.name || 'User'}
                  </h2>
                  <p className="text-gray-500 dark:text-[#7A8F8A] mb-4" data-id="element-4282">
                    {user.email}
                  </p>
                  <div className="flex flex-wrap gap-3" data-id="element-4283">
                    <div className="bg-gray-50 dark:bg-[#1A3330] px-4 py-2 rounded-lg" data-id="element-4284">
                      <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-4285">
                        Total Purchases
                      </p>
                      <p className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-4286">
                        0
                      </p>
                    </div>
                    <div className="bg-gray-50 dark:bg-[#1A3330] px-4 py-2 rounded-lg" data-id="element-4287">
                      <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-4288">
                        Total Downloads
                      </p>
                      <p className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-4289">
                        0
                      </p>
                    </div>
                    <div className="bg-gray-50 dark:bg-[#1A3330] px-4 py-2 rounded-lg" data-id="element-4290">
                      <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-4291">
                        Favorites
                      </p>
                      <p className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-4292">
                        {favoritesCount}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex-shrink-0 w-full md:w-auto" data-id="element-4293">
                <button onClick={() => navigate('/browse')} className="w-full md:w-auto bg-white dark:bg-[#1A3330] border border-[#2DB489] dark:border-[#4ED1A0] text-[#2DB489] dark:text-[#4ED1A0] px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#2DB489]/5 dark:hover:bg-[#4ED1A0]/10 transition-all duration-300 flex items-center justify-center whitespace-nowrap" data-id="element-4294">
                  <SearchIcon className="h-4 w-4 mr-2" data-id="element-4295" />
                  Browse Products
                </button>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mb-8 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-4296">
            <nav className="flex space-x-8 overflow-x-auto hide-scrollbar" data-id="element-4297">
              <button className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'purchases' ? 'border-[#2DB489] dark:border-[#4ED1A0] text-[#2DB489] dark:text-[#4ED1A0]' : 'border-transparent text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]'} transition-colors duration-200`} onClick={() => setActiveTab('purchases')} data-id="element-4298">
                <ShoppingCartIcon className="h-4 w-4 inline mr-2" data-id="element-4299" />
                My Purchases
              </button>
              <button className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'downloads' ? 'border-[#2DB489] dark:border-[#4ED1A0] text-[#2DB489] dark:text-[#4ED1A0]' : 'border-transparent text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]'} transition-colors duration-200`} onClick={() => setActiveTab('downloads')} data-id="element-4300">
                <DownloadIcon className="h-4 w-4 inline mr-2" data-id="element-4301" />
                Downloads
              </button>
              <button className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'orders' ? 'border-[#2DB489] dark:border-[#4ED1A0] text-[#2DB489] dark:text-[#4ED1A0]' : 'border-transparent text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]'} transition-colors duration-200`} onClick={() => setActiveTab('orders')} data-id="element-4302">
                <ClockIcon className="h-4 w-4 inline mr-2" data-id="element-4303" />
                Order History
              </button>
              <button className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'favorites' ? 'border-[#2DB489] dark:border-[#4ED1A0] text-[#2DB489] dark:text-[#4ED1A0]' : 'border-transparent text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]'} transition-colors duration-200`} onClick={() => setActiveTab('favorites')} data-id="element-4304">
                <HeartIcon className="h-4 w-4 inline mr-2" data-id="element-4305" />
                Favorites{' '}
                {favoritesCount > 0 && <span className="ml-1 bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-xs px-1.5 py-0.5 rounded-full" data-id="element-4306">
                    {favoritesCount}
                  </span>}
              </button>
              <button className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'notifications' ? 'border-[#2DB489] dark:border-[#4ED1A0] text-[#2DB489] dark:text-[#4ED1A0]' : 'border-transparent text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]'} transition-colors duration-200`} onClick={() => setActiveTab('notifications')} data-id="element-4307">
                <BellIcon className="h-4 w-4 inline mr-2" data-id="element-4308" />
                Notifications
              </button>
              <button className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'messages' ? 'border-[#2DB489] dark:border-[#4ED1A0] text-[#2DB489] dark:text-[#4ED1A0]' : 'border-transparent text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]'} transition-colors duration-200`} onClick={() => setActiveTab('messages')} data-id="element-4309">
                <MessageSquareIcon className="h-4 w-4 inline mr-2" data-id="element-4310" />
                Messages
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div data-id="element-4311">{renderTabContent()}</div>
        </div>
      </main>

      <Footer data-id="element-4312" />
    </div>;
}